/**
 * `<LastMarketCard>` — the market that just ended, under the order ticket, for
 * the whole of the next market.
 *
 * ```
 *   ⌄ LAST MARKET  21:46–21:47 UTC          ✓ SETTLED
 *   ▲ UP                         ▲ +$23.22 vs strike
 *   Settle  $63,441.28        Strike  $63,418.06
 *   print at 21:47:00
 *   ─────────────────────────────────────────────
 *   ✓ Result                                  final
 *   ✓ Paid out on chain                0x1234…cdef
 *   ─────────────────────────────────────────────
 *   Held  100 UP @ 63¢             Payout  $100.00
 *   Cost  $63.00                      Net  +$37.00
 * ```
 *
 * It replaces, from 1120px up, the two transient surfaces that told this story
 * in pieces: `<ResolutionPanel>` for the ~5 s after expiry and the receipt's
 * toast for the 6 s after that. The card goes `RESOLVING → RESOLVED → SETTLED`
 * in place and then stays, so a user who looked away still finds the answer
 * where they left the question. Below 1120px the old surfaces are unchanged;
 * pro mode has neither the card nor them, only the settlement toast — the
 * ladder keeps its height (2026-10-10).
 *
 * **It is about the market on this series, traded or not.** The result is the
 * market's; the bottom section is this account's part in it, from the
 * account's own `settlements` row once there is one — `Net` is the server's
 * number, never re-derived (§6.2 rule 2).
 *
 * **The arrow collapses it into one row at the foot of the column**, and the
 * ticket takes the height back. The choice is the route's to keep, because the
 * route decides what fills the column.
 *
 * ## What it subscribes, and when
 *
 * `market.{id}` for the market it describes and for the one on screen, the
 * series rail, `settlements` and `positions` — each of which changes a handful
 * of times a minute. The two subscriptions that tick, the index at 5 Hz and the
 * clock at 4 Hz, live in `<LiveResult>` and `<ResolvingPill>`, which are
 * mounted only for the seconds the print is being decided (invariant 2).
 */

import { createContext, memo, useContext, useId, useState } from 'react'
import { Glyph, NumCell, Pill } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { useIndex, useMarket, usePositions, useSeries, useSettlements } from '@/app/use-runtime'
import { useServerNow } from '@/app/use-server-now'
import {
  formatAvgCents,
  avgEntryCents,
  formatPriceString,
  formatShares,
  formatSignedUsdc,
  formatUsdc,
  formatUtcTime,
} from '@/lib/format'
import { formatFixed, parseFixed, payoutMicroUsdc, SHARES_DP } from '@/lib/decimal'
import { isMarketId, parseMarketId } from '@/lib/market-id'
import { observationFrom, toNumber, type Observation } from '@/domain/price'
import { leaningOutcome, observationSentence, resolutionView } from '@/domain/resolution'
import { lastMarketView, type LastMarketView, type MyResult } from '@/domain/last-market'
import type { MarketState } from '@/domain/lifecycle'

type Row = Record<string, unknown>

export interface LastMarketCardProps {
  /** The market that ended at the current anchor. `null` before the runtime
   *  knows an anchor. */
  marketId: string | null
  seriesId: string
  /** The asset's index channel, for the live estimate while resolving. */
  symbol: string
  /** The market on screen — its strike is this one's settlement print. */
  currentMarketId: string | null
  /** `period_seconds`, for the time range when nothing else has said. */
  periodS: number
  /** The user folded it — the only thing that ever folds it. */
  collapsed: boolean
  onToggle: () => void
  stale?: boolean
  className?: string
}

export const LastMarketCard = memo(function LastMarketCard({
  marketId,
  seriesId,
  symbol,
  currentMarketId,
  periodS,
  collapsed,
  onToggle,
  stale = false,
  className,
}: LastMarketCardProps) {
  const market = useMarket(marketId)
  const successor = useMarket(currentMarketId)
  const series = useSeries(seriesId)
  const { rows: settlements } = useSettlements<Row>()
  const { rows: positions } = usePositions<Row>()
  const bodyId = useId()

  const view =
    marketId === null ? null : (
      lastMarketView({
        marketId,
        market,
        railEntry: railEntryOf(series, marketId),
        successor,
        settlement: settlements.find((row) => row.market_id === marketId) ?? null,
        position: positions.find((row) => row.market_id === marketId) ?? null,
      })
    )

  const range =
    marketId !== null && isMarketId(marketId) ?
      rangeOf(parseMarketId(marketId).anchorMs, view?.expiryAtMs ?? null, periodS)
    : null

  /**
   * **One height, whatever it says, and only the user folds it** (2026-10-13).
   *
   * The card used to measure the room the ticket left it and fold itself when
   * that was short. The ticket's height moves with live data — the
   * partial-fill notice comes and goes with the book — so the card folded and
   * unfolded with the book, and every row added to the ticket moved where it
   * folded. Now nothing here is measured: the body is as tall as the tallest
   * thing the card ever says (`<Sizer>`, laid in the same grid cell, invisible),
   * so it is one height through RESOLVING, RESOLVED and SETTLED, held or not.
   * The route caps it at a share of the column; past that, and only then, the
   * body scrolls. The ticket takes the rest of the column and scrolls itself,
   * with its primary button kept in view (`OrderTicket`).
   */
  return (
    <section
      data-testid="last-market-card"
      data-market={marketId ?? ''}
      data-phase={view?.phase ?? 'UNKNOWN'}
      data-collapsed={collapsed ? 'true' : 'false'}
      data-stale={stale ? 'true' : undefined}
      aria-label="last market"
      className={cx(
        'flex min-h-0 flex-col overflow-hidden bg-bg-surface',
        'data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        aria-label={collapsed ? 'show last market' : 'hide last market'}
        className={cx(
          'flex h-[var(--ud-row-height-comfortable)] w-full shrink-0 items-center gap-2 px-2 text-left text-caption text-text-secondary',
          'hover:bg-bg-hover focus-visible:shadow-focus focus-visible:outline-none',
          !collapsed && 'border-b border-border-subtle',
        )}
      >
        <Chevron up={collapsed} />
        <span className="shrink-0 uppercase">Last market</span>
        {range ?
          <span className="ud-num shrink-0 text-text-tertiary">{collapsed ? range.short : range.long}</span>
        : null}
        <span className="ml-auto flex min-w-0 items-center gap-1_5">
          {view === null ?
            null
          : collapsed ?
            <CollapsedSummary view={view} />
          : <StatusPill view={view} muted={stale} />}
        </span>
      </button>

      {collapsed ? null : (
        <div id={bodyId} className="ud-scroll min-h-0 flex-1">
          <div className="grid">
            <Sizer />
            <div className="flex flex-col gap-2 px-3 py-2 [grid-area:1/1]">
              {view === null || view.phase === 'UNKNOWN' ?
                <span className="text-body-sm text-text-tertiary">—</span>
              : <>
                  {view.phase === 'RESOLVING' ?
                    <LiveBody view={view} symbol={symbol} />
                  : <Body view={view} settle={view.settlePrice} outcome={view.outcome} provisional={false} />}
                </>
              }
            </div>
          </div>
        </div>
      )}
    </section>
  )
})

/**
 * The tallest thing the card says, drawn with its own components and never
 * seen: a final result with its print, both steps, and a position held on both
 * sides with its payout line. Laid in the body's one grid cell under the real
 * content, it makes the body that tall whatever the real content is — and a
 * row added to any of these components grows it too, with nothing to keep in
 * step by hand.
 */
const Sizer = memo(function Sizer() {
  return (
    <div aria-hidden inert className="invisible flex flex-col gap-2 px-3 py-2 [grid-area:1/1]">
      <InSizer.Provider value>
        <Body view={SIZER_VIEW} settle={SIZER_VIEW.settlePrice} outcome="UP" provisional={false} />
      </InSizer.Provider>
    </div>
  )
})

/** Inside `<Sizer>`, which names nothing: a test or a tool looking for the
 *  card's parts finds the ones on screen, once. */
const InSizer = createContext(false)

function useTestId(id: string): string | undefined {
  return useContext(InSizer) ? undefined : id
}

const SIZER_PRICE = { value: '00,000.00', raw: '0', expo: -2 }

const SIZER_VIEW: LastMarketView = {
  marketId: '',
  state: null,
  phase: 'RESOLVED',
  degraded: false,
  outcome: 'UP',
  strike: SIZER_PRICE,
  settlePrice: SIZER_PRICE,
  expiryAtMs: 0,
  printAtMs: 0,
  settlementWindowStartMs: null,
  resolveEtaMs: null,
  traded: true,
  chain: 'WAITING',
  txHash: null,
  settledAtMs: null,
  mine: { kind: 'HELD', upShares: '1.000000', downShares: '1.000000', costBasis: '1.000000' },
}

// ---------------------------------------------------------------------------
// The header
// ---------------------------------------------------------------------------

function StatusPill({ view, muted }: { view: LastMarketView; muted: boolean }) {
  const mutedProp = muted ? { muted: true } : {}
  switch (view.phase) {
    case 'RESOLVING':
      return <ResolvingPill view={view} muted={muted} />
    case 'RESOLVED':
      return view.chain === 'SETTLING' ?
          <Pill tone="info" dot {...mutedProp}>
            SETTLING
          </Pill>
        : <Pill tone="neutral" {...mutedProp}>
            RESOLVED
          </Pill>
    case 'SETTLED':
      return (
        <Pill tone="neutral" {...mutedProp}>
          <Glyph name="check" label={null} /> SETTLED
        </Pill>
      )
    case 'VOID':
      return (
        <Pill tone="neutral" {...mutedProp}>
          <Glyph name="check" label={null} /> VOIDED
        </Pill>
      )
    default:
      return null
  }
}

/** The determinate bar over `[settlement window start, resolve_eta]` — the
 *  same arc `<ResolutionPanel>` draws, with its own 4 Hz clock. */
function ResolvingPill({ view, muted }: { view: LastMarketView; muted: boolean }) {
  const nowMs = useServerNow()
  const phase = resolutionView({
    state: (view.state ?? 'RESOLVING') as MarketState,
    expiryAtMs: view.expiryAtMs ?? 0,
    settlementStartMs: view.settlementWindowStartMs ?? 0,
    resolveEtaMs: view.resolveEtaMs,
    nowMs,
  })
  return (
    <Pill tone={view.degraded ? 'warning' : 'info'} progress={phase.progress} {...(muted ? { muted: true } : {})}>
      {view.degraded ? 'RESOLVING · DEGRADED' : 'RESOLVING'}
    </Pill>
  )
}

/** One line: the outcome, the state and this account's net. */
function CollapsedSummary({ view }: { view: LastMarketView }) {
  const net = view.mine?.kind === 'SETTLED' ? view.mine.netPnl : null
  return (
    <>
      {view.outcome !== null ?
        <span className={cx('ud-num shrink-0 font-medium', view.outcome === 'UP' ? 'text-up-text' : 'text-down-text')}>
          <Glyph name={view.outcome === 'UP' ? 'up' : 'down'} label={null} /> {view.outcome}
        </span>
      : null}
      <span className="truncate text-text-tertiary">{summaryWord(view)}</span>
      {net !== null ?
        <span className={cx('ud-num shrink-0', net.trimStart().startsWith('-') ? 'text-down-text' : 'text-up-text')}>
          {formatSignedUsdc(net)}
        </span>
      : null}
    </>
  )
}

function summaryWord(view: LastMarketView): string {
  switch (view.phase) {
    case 'RESOLVING':
      return 'resolving'
    case 'RESOLVED':
      return view.chain === 'SETTLING' ? 'settling' : 'resolved'
    case 'SETTLED':
      return 'settled'
    case 'VOID':
      return 'voided'
    default:
      return ''
  }
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

/**
 * The print being decided, as `<ResolutionPanel>` follows it: the index tick
 * stamped at the settlement instant once it arrives, the newest one before it
 * until then (`SettlementPrint`'s rule). Mounted only while the market is
 * resolving, which is the only time the card needs a 5 Hz price.
 *
 * **Held in state and advanced during render**, React's own pattern for state
 * derived from an input that changes, rather than from an effect: an effect
 * paints one frame of the previous estimate after every tick. `observed_at_ms`
 * is the oracle's stamp, never the arrival time — a tick that queued behind the
 * boundary burst still describes the instant it was printed.
 */
function LiveBody({ view, symbol }: { view: LastMarketView; symbol: string }) {
  const index = useIndex(symbol)
  const atMs = view.printAtMs ?? 0
  const key = `${view.marketId}:${atMs}`
  const [held, setHeld] = useState<HeldPrint>(() => ({
    key,
    atMs: -Infinity,
    value: null,
  }))

  const current = held.key === key ? held : { key, atMs: -Infinity, value: null }
  const observedAtMs = numberOf(index, 'observed_at_ms')
  const tick =
    observedAtMs !== null && observedAtMs <= atMs && observedAtMs > current.atMs ? observationFrom(index) : null
  const next = tick !== null && observedAtMs !== null ? { key, atMs: observedAtMs, value: tick } : current
  if (next !== held) setHeld(next)

  return <Body view={view} settle={next.value} outcome={leaningOutcome(next.value, view.strike)} provisional />
}

interface BodyProps {
  view: LastMarketView
  settle: Observation | null
  /** Final, or leaning while `provisional`. */
  outcome: 'UP' | 'DOWN' | null
  /** `LEANING`, never final, until the venue says `RESOLVED` (§2.8). */
  provisional: boolean
}

function Body(props: BodyProps) {
  return (
    <>
      <Result {...props} />
      <Settlement view={props.view} />
      <Mine view={props.view} outcome={props.outcome} provisional={props.provisional} />
    </>
  )
}

interface HeldPrint {
  key: string
  /** The oracle stamp of `value`; nothing at or before it replaces it. */
  atMs: number
  value: Observation | null
}

function Result({ view, settle, outcome, provisional }: BodyProps) {
  const testId = useTestId('last-market-result')
  if (view.phase === 'VOID') {
    return (
      <div className="flex flex-col gap-0_5" data-testid={testId}>
        <span className="text-title-sm font-semibold text-text-primary">Voided</span>
        <span className="text-body-sm text-text-secondary">every order refunded at entry cost</span>
      </div>
    )
  }
  const delta = deltaOf(settle, view.strike)
  // Three lines, because the card has to fit under the ticket on a 900px
  // window: the outcome and how far from the strike, the two prices, and the
  // instant of the print.
  return (
    <div className="flex flex-col gap-0_5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cx(
            'ud-num font-semibold',
            provisional ? 'text-body-sm uppercase' : 'text-title-sm',
            outcome === null ? 'text-text-tertiary'
            : outcome === 'UP' ? 'text-up-text'
            : 'text-down-text',
          )}
        >
          {outcome === null ?
            provisional ?
              'confirming'
            : '—'
          : <>
              {provisional ? 'LEANING ' : null}
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} /> {outcome}
            </>
          }
        </span>
        {delta === null ? null : (
          <span className={cx('ud-num text-body-sm', delta >= 0 ? 'text-up-text' : 'text-down-text')}>
            <Glyph name={delta >= 0 ? 'up' : 'down'} /> {signedFixed(delta, 2)}{' '}
            <span className="text-text-tertiary">vs strike</span>
          </span>
        )}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex items-baseline gap-1_5">
          <span className="text-micro text-text-tertiary">Settle</span>
          <NumCell
            align="left"
            value={settle ? formatPriceString(settle.value) : '—'}
            {...(settle ? compareProp(settle) : {})}
            className="text-body-sm text-text-primary"
          />
        </span>
        <span className="flex items-baseline gap-1_5">
          <span className="text-micro text-text-tertiary">Strike</span>
          <span className="ud-num text-body-sm text-text-secondary">
            {view.strike ? formatPriceString(view.strike.value) : '—'}
          </span>
        </span>
      </div>
      {view.printAtMs !== null ?
        <span className="text-caption text-text-tertiary">{observationSentence(view.printAtMs)}</span>
      : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The settlement
// ---------------------------------------------------------------------------

type StepState = 'done' | 'active' | 'pending' | 'none'

function Settlement({ view }: { view: LastMarketView }) {
  const result: { state: StepState; detail: string } =
    view.phase === 'VOID' ? { state: 'done', detail: 'voided' }
    : view.phase === 'RESOLVED' || view.phase === 'SETTLED' ? { state: 'done', detail: 'final' }
    : view.phase === 'RESOLVING' ?
      {
        state: 'active',
        detail: view.degraded ? 'degraded — will still settle' : 'confirming',
      }
    : { state: 'pending', detail: '—' }

  const chain: { state: StepState; detail: string; title?: string } =
    view.chain === 'SETTLED' ?
      view.txHash ?
        {
          state: 'done',
          detail: truncateHash(view.txHash),
          title: view.txHash,
        }
        // Whether it traded is unknown only on a page that arrived after
        // `market.{id}` was dropped; "settled" claims no money moved.
      : view.traded === null ? { state: 'done', detail: 'settled' }
      : {
          state: 'done',
          detail: view.settledAtMs !== null ? formatUtcTime(view.settledAtMs) : 'done',
        }
    : view.chain === 'SETTLING' ? { state: 'active', detail: 'settling' }
    : view.chain === 'NOTHING' ? { state: 'none', detail: 'no trades' }
    : view.chain === 'VOID' ? { state: 'done', detail: 'refunded at entry cost' }
    : { state: 'pending', detail: 'after the result' }
  const testId = useTestId('last-market-settlement')
  const chainTestId = useTestId('last-market-chain')

  return (
    <div className="flex flex-col gap-0_5 border-t border-border-subtle pt-2" data-testid={testId}>
      <Step label="Result" {...result} />
      <Step label="Paid out on chain" {...chain} {...(chainTestId ? { testId: chainTestId } : {})} />
    </div>
  )
}

function Step({
  label,
  state,
  detail,
  title,
  testId,
}: {
  label: string
  state: StepState
  detail: string
  title?: string
  testId?: string
}) {
  return (
    <div
      className="flex items-center gap-2 text-body-sm"
      data-step={state}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <StepMark state={state} />
      <span className={cx('shrink-0 whitespace-nowrap', state === 'done' ? 'text-text-primary' : 'text-text-secondary')}>
        {label}
      </span>
      <span
        className={cx('ud-num ml-auto truncate', state === 'active' ? 'text-info-text' : 'text-text-tertiary')}
        title={title}
      >
        {detail}
      </span>
    </div>
  )
}

function StepMark({ state }: { state: StepState }) {
  if (state === 'done') {
    return (
      <span className="inline-flex size-4 shrink-0 items-center justify-center text-success-text">
        <Glyph name="check" label={null} />
      </span>
    )
  }
  return (
    <span aria-hidden="true" className="inline-flex size-4 shrink-0 items-center justify-center">
      <span
        className={cx(
          'inline-block size-1_5 rounded-full',
          state === 'active' ? 'animate-pulse bg-info-solid'
          : state === 'none' ? 'bg-border-strong'
          : 'border border-border-strong',
        )}
      />
    </span>
  )
}

// ---------------------------------------------------------------------------
// This account
// ---------------------------------------------------------------------------

function Mine({
  view,
  outcome,
  provisional,
}: {
  view: LastMarketView
  outcome: 'UP' | 'DOWN' | null
  provisional: boolean
}) {
  const mine = view.mine
  const testId = useTestId('last-market-mine')
  return (
    <div className="flex flex-col gap-0_5 border-t border-border-subtle pt-2" data-testid={testId}>
      {mine === null ?
        <span className="text-body-sm text-text-tertiary">No position in this market</span>
      : mine.kind === 'SETTLED' ?
        <SettledFigures mine={mine} />
      : <HeldFigures mine={mine} view={view} outcome={outcome} provisional={provisional} />}
    </div>
  )
}

function SettledFigures({ mine }: { mine: Extract<MyResult, { kind: 'SETTLED' }> }) {
  const avg = mine.shares ? avgEntryCents(mine.costBasis, mine.shares) : null
  const negative = mine.netPnl.trimStart().startsWith('-')
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0_5">
      <Figure
        label="Held"
        value={
          mine.shares ?
            `${formatShares(mine.shares)}${mine.myOutcome ? ` ${mine.myOutcome}` : ''}${avg ? ` @ ${formatAvgCents(avg)}` : ''}`
          : '—'
        }
      />
      <Figure label="Payout" value={formatUsdc(mine.payout)} align="right" />
      <Figure label="Cost" value={formatUsdc(mine.costBasis)} />
      <Figure label="Net" value={formatSignedUsdc(mine.netPnl)} align="right" tone={negative ? 'down' : 'up'} />
    </div>
  )
}

/**
 * Held, before the settlement row arrives: what it pays if the result holds,
 * or what is being paid once it has. `<ResolutionPanel>`'s forecast line, on
 * the same exact arithmetic — one winning share is one dollar.
 */
function HeldFigures({
  mine,
  view,
  outcome,
  provisional,
}: {
  mine: Extract<MyResult, { kind: 'HELD' }>
  view: LastMarketView
  outcome: 'UP' | 'DOWN' | null
  provisional: boolean
}) {
  const sides = [
    mine.upShares ? `${formatShares(mine.upShares)} UP` : null,
    mine.downShares ? `${formatShares(mine.downShares)} DOWN` : null,
  ].filter((side): side is string => side !== null)

  const winning =
    outcome === 'UP' ? mine.upShares
    : outcome === 'DOWN' ? mine.downShares
    : null
  const payout = outcome === null ? null : formatUsdc(formatFixed(payoutMicroUsdc(sharesOf(winning)), 6))
  const when =
    provisional ? 'if it holds'
    : view.phase === 'SETTLED' ? 'paid'
    : view.phase === 'RESOLVED' ? 'being paid'
    : null

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0_5">
      <Figure label="Held" value={sides.join(' · ')} />
      <Figure label="Cost" value={mine.costBasis ? formatUsdc(mine.costBasis) : '—'} align="right" />
      {payout !== null && when !== null ?
        <span className="col-span-2 text-body-sm text-text-secondary">
          <span className="ud-num text-text-primary">{payout}</span> {when}
        </span>
      : null}
    </div>
  )
}

function Figure({
  label,
  value,
  align = 'left',
  tone,
}: {
  label: string
  value: string
  align?: 'left' | 'right'
  tone?: 'up' | 'down'
}) {
  return (
    <span className={cx('flex items-baseline gap-1_5', align === 'right' && 'justify-end')}>
      <span className="text-micro text-text-tertiary">{label}</span>
      <span
        className={cx(
          'ud-num truncate text-body-sm',
          tone === 'up' ? 'text-up-text'
          : tone === 'down' ? 'text-down-text'
          : 'text-text-primary',
        )}
      >
        {value}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------

/** The arrow at the top of the card: down to fold it away, up to bring it back. */
function Chevron({ up }: { up: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="size-4 shrink-0 text-text-tertiary" fill="none" aria-hidden="true">
      <path
        d={up ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function numberOf(source: unknown, field: string): number | null {
  const value = (source as Row | null)?.[field]
  return typeof value === 'number' ? value : null
}

function railEntryOf(series: Row | null, marketId: string): Row | null {
  const rows = series?.markets
  if (!Array.isArray(rows)) return null
  return (rows as Row[]).find((row) => row.market_id === marketId) ?? null
}

/** `21:46–21:47 UTC`, and `21:46` alone where a row has no room for both. */
function rangeOf(anchorMs: number, expiryAtMs: number | null, periodS: number): { long: string; short: string } {
  const start = formatUtcTime(anchorMs).slice(0, 5)
  const end = formatUtcTime(expiryAtMs ?? anchorMs + periodS * 1000).slice(0, 5)
  return { long: `${start}–${end} UTC`, short: start }
}

function sharesOf(value: string | null): bigint {
  if (value === null) return 0n
  try {
    return parseFixed(value, SHARES_DP)
  } catch {
    return 0n
  }
}

/** For the Δ line only — a difference of two prices, in pixels' worth of
 *  precision. The outcome itself is decided on `raw` (`leaningOutcome`). */
function deltaOf(settle: Observation | null, strike: Observation | null): number | null {
  const a = toNumber(settle)
  const b = toNumber(strike)
  return a === null || b === null ? null : a - b
}

function signedFixed(value: number, dp: number): string {
  return `${value < 0 ? '-' : '+'}$${Math.abs(value).toFixed(dp)}`
}

/** The flash's ordering key, exact (`<ResolutionPanel>`'s `compareProp`). */
function compareProp(observation: Observation): { compare?: bigint } {
  try {
    return { compare: BigInt(observation.raw) }
  } catch {
    return {}
  }
}

/** `0x1234…cdef` — head and tail, because the tail is what distinguishes two. */
function truncateHash(hash: string): string {
  return hash.length <= 18 ? hash : `${hash.slice(0, 8)}…${hash.slice(-6)}`
}
